import React from 'react';
import { Flex, SegmentedControl, SegmentedControlProps, Text } from '@mantine/core';
import { Controller } from 'react-hook-form';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';

interface Props extends Omit<SegmentedControlProps, 'value'> {
	name: string;
	data: { value: string; label: React.ReactNode; disabled?: boolean }[];
	selectedIcon?: React.ReactNode;
	handleAsBoolean?: boolean;
}

export const FormSegmentedControl: React.FC<Props> = ({
	name,
	data,
	handleAsBoolean = false,
	onChange,
	...rest
}) => {
	return (
		<Controller
			name={name}
			render={({ field }) => {
				const currentValue = field.value;

				const dataWithIcons = data.map((item) => {
					const isSelected = item.value === String(currentValue);

					return {
						...item,
						label: (
							<Flex align="center" gap="0.5rem" justify="start">
								<Icon
									variant="tick"
									style={{
										opacity: isSelected ? 1 : 0,
										transition: 'opacity 0.1s',
									}}
								/>
								<Text size="md">{item.label}</Text>
							</Flex>
						),
					};
				});

				let componentValue: string;
				// Mantine component doesn't support boolean values so we need to handle it ourselves
				if (handleAsBoolean) {
					if (currentValue === true) {
						componentValue = 'true';
					} else if (currentValue === false) {
						componentValue = 'false';
					} else {
						componentValue = '';
					}
				} else {
					componentValue =
						currentValue !== null && currentValue !== undefined
							? String(currentValue)
							: '';
				}

				return (
					<SegmentedControl
						{...field}
						value={componentValue}
						onChange={(selectedValue) => {
							onChange?.(selectedValue);
							if (handleAsBoolean) {
								const boolValue = selectedValue === 'true';
								field.onChange(boolValue);
							} else {
								field.onChange(selectedValue);
							}
						}}
						data={dataWithIcons}
						bg={rest.disabled ? 'gray.4' : 'white.0'}
						fullWidth
						sx={(theme) => ({
							'&': rest.disabled
								? {
										borderColor: theme.colors.gray[8],
										opacity: 0.6,
									}
								: {},
							'.mantine-SegmentedControl-indicator': {
								backgroundColor: rest.disabled
									? theme.colors.gray[8]
									: theme.colors.blue[0],
								border: `1px solid ${
									rest.disabled ? theme.colors.gray[8] : theme.colors.blue[4]
								}`,
								width: '50%',
								color: rest.disabled ? theme.colors.dark[0] : theme.colors.dark[8],
							},
							'& .mantine-SegmentedControl-label, & .mantine-SegmentedControl-label:hover':
								{
									color: rest.disabled
										? theme.colors.dark[0]
										: theme.colors.dark[8],
								},
						})}
						{...rest}
					/>
				);
			}}
		/>
	);
};
